import { useTranslation } from "react-i18next";

import type { MissionNotificationSchema } from "@/services/mission-notifications";
import {
  isPoolExhaustedNotification,
  isSecondaryTaskNotification,
} from "@/services/mission-notifications";
import { Notification } from "./notification";
import { useToastCampaign } from "./use-toast-campaign";

/**
 * Copy for one mission notification.
 *
 * A reward is the whole message: "You won · <prize>" on the gold card, no
 * supporting line — the win speaks for itself, and naming the task under it
 * just diluted the moment — except to say a held Sprint Mission payout comes
 * when the campaign ends. Without a reward, the event leads and the second
 * line says what happens next.
 *
 * Deliberately free of `mission_order`: the backoffice assigns it as
 * `highest + 1`, so it gaps when a mission is deleted and "step 4 of 3" is
 * reachable. The repetition counters and `remaining_tasks_amount` are real
 * counts, so those carry the progress.
 */
function useCopy(notification: MissionNotificationSchema): {
  title: string;
  label?: string;
  text?: string;
  variant: "default" | "prize" | "task";
} | null {
  const { t } = useTranslation();

  // Sprint Mission: the pool emptied. Either this player finished after the last slot went
  // (the race loser) or they were mid-mission when it did — same message, the run is over.
  // Carries no prize or task fields, so it is handled before those are read.
  if (isPoolExhaustedNotification(notification)) {
    return {
      title: t("notification.sprintMission.noMoreWinners", "No more winners"),
      text: t(
        "notification.sprintMission.over",
        "All winners are in, this one is over"
      ),
      variant: "default",
    };
  }

  // Both already localized + currency-formatted server-side (see the mission
  // Notification classes' `render`), so they go out verbatim.
  const prize = notification.prize_title;
  const task = isSecondaryTaskNotification(notification)
    ? notification.task_title
    : "";

  if (prize) {
    // `prizes.youWon` rather than a notification-local string: it already ships
    // translated in every locale, and a reward reads the same wherever it lands.
    // A Sprint Mission pool that holds its payouts credits the prize at the end,
    // and the card says so rather than implying it landed now.
    const paidAtEnd =
      !isSecondaryTaskNotification(notification) &&
      notification.payout_at_campaign_end === true;
    return {
      title: prize,
      label: t("prizes.youWon"),
      text: paidAtEnd ? t("sprintMission.paidOutWhenItEnds") : undefined,
      variant: "prize",
    };
  }

  // No reward, but we know what the player finished: the task takes the chip.
  if (task) {
    return {
      title: task,
      label: t("notification.mission.taskComplete"),
      text: t("notification.mission.keepGoing"),
      variant: "task",
    };
  }

  // Neither a reward nor a task name (a secondary task whose task was deleted
  // renders bare, and mission completions land here): the event leads.
  if (isSecondaryTaskNotification(notification)) {
    return {
      title: t("notification.mission.taskComplete"),
      text: t("notification.mission.keepGoing"),
      variant: "default",
    };
  }

  // Mission completions: only the finished ladder and a repeated round carry
  // copy — a plain mid-ladder completion without a prize has nothing to say
  // and is filtered out before it reaches the toaster (see
  // `isToastableMissionNotification`); `null` here is the belt-and-braces.
  if (notification.remaining_tasks_amount === 0) {
    return { title: t("notification.mission.allComplete"), variant: "default" };
  }
  if (notification.number_of_repetitions > 1) {
    return {
      title: t("notification.mission.roundComplete", {
        current: notification.completed_repetitions,
        total: notification.number_of_repetitions,
      }),
      variant: "default",
    };
  }
  return null;
}

/**
 * A mission notification as a toast. Tapping it opens the campaign the
 * notification came from (`useToastCampaign`).
 */
export const MissionToast = ({
  notification,
  onDismiss,
}: {
  notification: MissionNotificationSchema;
  onDismiss: () => void;
}) => {
  const copy = useCopy(notification);
  const { imageUrl, onClick } = useToastCampaign(
    notification.campaign_id,
    onDismiss
  );

  if (!copy) return null;

  return (
    <Notification
      title={copy.title}
      label={copy.label}
      text={copy.text}
      imageUrl={imageUrl}
      variant={copy.variant}
      onClick={onClick}
    />
  );
};
